import { DATE_FORMATS, formatDate } from '@frontend/mwm-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import type { FormEvent, ReactElement } from 'react'
import type { IApplicationVersionLocale, TFileType } from '~/types/application-version'
import type { DeviceType } from '~/types/device-type'
import type { IFileUploaded } from '~/types/file'

import MediaCardWithCall from './MediaCardWithCall'
import UploadFilesBlock from './UploadFilesBlock'

import AppLogo from '~/components/AppLogo'
import Badge from '~/components/Badge'
import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import TextArea from '~/components/Inputs/Basic/TextArea'
import TextInput from '~/components/Inputs/Basic/TextInput'
import MediaCard from '~/components/MediaCard'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import Truncate from '~/components/Truncate'
import UserCardWithCall from '~/components/UserCardWithCall'
import { FASTLY_PATH } from '~/const/asset'
import { useFilesDetailsQuery } from '~/queries/files'
import { useGetMeAsUserQuery } from '~/queries/users'
import {
  keys,
  useDeleteAppVersionComment,
  useSetAppVersionCommentsMutation,
  useSetAppVersionNoteMutation,
  useVersionCommentsQuery,
  useVersionQuery,
} from '~/queries/versions'
import { handleGenericError } from '~/utils/error'
import { resize } from '~/utils/media'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  deviceType?: DeviceType
  externalId?: string
  locales: IApplicationVersionLocale[]
  semanticVersion: string
}

const PanelContent = ({ deviceType, externalId, locales, semanticVersion }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [selectedLocale, setSelectedLocale] = useState<IApplicationVersionLocale>({ country: 'US', language: 'en-US' })

  const [isNoteEditable, setIsNoteEditable] = useState(false)

  const [note, setNote] = useState('')

  const [noteFiles, setNoteFiles] = useState<IFileUploaded[]>([])

  const [comment, setComment] = useState('')

  const [commentFiles, setCommentFiles] = useState<IFileUploaded[]>([])

  const [isNoteInputVisible, setIsNoteInputVisible] = useState(false)

  const [isConformationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const [commentToDelete, setCommentToDelete] = useState('')

  /** Queries & Mutations */

  const { data: versionDetails, isLoading: isVersionDetailsLoading } = useVersionQuery({
    country: selectedLocale.country,
    deviceType,
    externalId,
    lang: selectedLocale.language,
    semanticVersion,
  })

  const { data: versionCommentsList, isLoading: isVersionCommentsListLoading } = useVersionCommentsQuery({
    deviceType,
    externalId,
    limit: 1000,
    semanticVersion,
  })

  const { data: noteFilesDetails, isLoading: isNoteFilesDetailsLoading } = useFilesDetailsQuery(
    versionDetails?.note?.file_ids || []
  )

  const { data: currentUserProfile } = useGetMeAsUserQuery()

  const { mutateAsync: setAppVersionNote } = useSetAppVersionNoteMutation()

  const { mutateAsync: setAppVersionComment } = useSetAppVersionCommentsMutation()

  const { mutateAsync: deleteVersionComment } = useDeleteAppVersionComment()

  /** Handlers */

  const handleChangeLocale = (selectedOption: IOption<IApplicationVersionLocale>) => {
    const _selectedLocale = locales.find(
      ({ country, language }) => language === selectedOption.value.language && country === selectedOption.value.country
    )

    if (_selectedLocale) {
      setSelectedLocale(_selectedLocale)
    }
  }

  const options = locales.map((l) => ({
    label: `${l.language} - (${l.country})`,
    value: l,
  }))

  const handleChange = (event: FormEvent<HTMLTextAreaElement | HTMLInputElement>, type: TFileType) =>
    type === 'comment' ? setComment(event.currentTarget.value) : setNote(event.currentTarget.value)

  const handleDeleteFile = (fileType: TFileType, idToDelete: string) =>
    fileType === 'comment'
      ? setCommentFiles((prev) => prev.filter(({ id }) => id !== idToDelete))
      : setNoteFiles((prev) => prev.filter(({ id }) => id !== idToDelete))

  //NOTE

  const handleNoteFilesDrop = (files: IFileUploaded[]) => setNoteFiles((prev) => [...prev, ...files])

  const handleAddOrEditNote = () => {
    setIsNoteInputVisible(true)
    setIsNoteEditable(true)
  }

  const handleResetNote = (resetValues?: boolean) => {
    setIsNoteEditable(false)
    setIsNoteInputVisible(false)
    resetValues && setNoteFiles([])
  }

  //COMMENT

  const handleCommentFilesDrop = (files: IFileUploaded[]) => setCommentFiles((prev) => [...prev, ...files])

  const handleDeleteComment = (commentId: string) => () => {
    handleDialogModalState(true)
    setCommentToDelete(commentId)
  }

  const handleDialogModalState = (value: boolean) => setIsConfirmationModalOpen(value)

  const handleResetComment = () => {
    setCommentFiles([])
    setComment('')
  }

  const handleSubmitNote = async () => {
    if (deviceType && externalId) {
      try {
        await setAppVersionNote({
          content: note.trim(),
          deviceType,
          externalId,
          fileIds: noteFiles.map(({ id }) => id),
          semanticVersion,
        })

        await queryClient.invalidateQueries({ queryKey: keys.detailsV2({ deviceType, externalId, semanticVersion }) })
        handleResetNote(true)
        displayToast('Your note has successfully been sent', 'success')
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleSubmitComment = async () => {
    if (deviceType && externalId) {
      try {
        await setAppVersionComment({
          content: comment.trim(),
          deviceType,
          externalId,
          fileIds: commentFiles.map(({ id }) => id),
          semanticVersion,
        })

        await queryClient.invalidateQueries({ queryKey: keys.comments({ deviceType, externalId, semanticVersion }) })
        handleResetComment()
        displayToast('Your comment has successfully been sent', 'success')
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleDeleteCommentConfirm = async () => {
    try {
      await deleteVersionComment(commentToDelete)
      await queryClient.invalidateQueries({ queryKey: keys.comments({ deviceType, externalId, semanticVersion }) })

      setIsConfirmationModalOpen(false)
      handleResetComment()
      displayToast('Your comment has successfully been deleted', 'success')
    } catch (e) {
      handleGenericError(e)
    }
  }

  /** Effects */

  useEffect(() => {
    if (versionDetails?.note?.content) {
      setNote(versionDetails.note.content)
    }
  }, [versionDetails?.note?.content, versionDetails?.note?.file_ids])

  useEffect(() => {
    if (noteFilesDetails && noteFilesDetails.length > 0) {
      setNoteFiles(noteFilesDetails)
    } else {
      setNoteFiles([])
    }
  }, [noteFilesDetails])

  /** Render */

  return isVersionDetailsLoading && isVersionCommentsListLoading ? (
    <MwMLoader category="item" />
  ) : versionDetails ? (
    <div className="version-panel-content">
      <div className="version-panel-content__header">
        <div className="version-panel-content__header__title-container">
          <span className="version-panel-content__header__title">Version {versionDetails.semantic_version}</span>
        </div>
        <div className="version-panel-content__header__date">
          {formatDate(versionDetails.release_date, DATE_FORMATS.DEFAULT_WITH_HOURS)}
        </div>
        <SelectInput
          className="version-panel-content__header__locale-select-input"
          onChange={(value) => handleChangeLocale(value as unknown as IOption<IApplicationVersionLocale>)}
          options={options}
          value={options.find(
            (option) =>
              option.value.country === selectedLocale.country && option.value.language === selectedLocale.language
          )}
        />
      </div>
      <div className="version-panel-content__separator" />
      <div className="version-panel-content__body">
        <div className="app-container">
          <AppLogo
            appLogoUrl={resize(`${FASTLY_PATH}${versionDetails.icon_file_id}`, 715, 715)}
            appPlatform={versionDetails.device_type}
            className="app-logo"
            size="medium"
          />
          <div>
            <div className="app-icon-name">{versionDetails?.display_name}</div>
            <div
              className="app-icon-subtitle"
              dangerouslySetInnerHTML={{ __html: versionDetails?.short_description }}
            />
          </div>
        </div>
        <div className="keyword-container">
          {versionDetails.keywords?.length ? (
            <div className="keyword-items">
              {versionDetails.keywords.map((element: string, index: number) => (
                <Badge className="badge" key={index} outlined size="medium" value={element} />
              ))}
            </div>
          ) : null}
        </div>
        <div className="preview-image-bloc">
          {versionDetails.media.screenshots.length
            ? Object.values(versionDetails.media.screenshots).map((src, idx) => (
                <img className="preview" key={src + idx} src={resize(`${FASTLY_PATH}${src}`, 0.6, 0.6)} />
              ))
            : null}
        </div>
        <div className="section-title">What&apos;s new?</div>
        <div className="section-content">
          <Truncate>{versionDetails.changelog || 'Nothing new'}</Truncate>
        </div>
        <div className="section-title">Description</div>
        <div className="section-content">
          <Truncate>{versionDetails.description}</Truncate>
        </div>
        <div className="version-panel-content__separator" />
        <div className="section-title">
          MWM notes
          <Button disabled={isNoteEditable} onClick={handleAddOrEditNote} outlined size="small">
            {versionDetails.note?.content !== '' ? 'Edit' : '+ Add'}
          </Button>
        </div>
        <div className="section-content">
          <div className="section-content__note">
            {versionDetails.note?.content && !isNoteInputVisible ? (
              <div className="section-content__note__text">{versionDetails.note?.content}</div>
            ) : null}
            {isNoteInputVisible && isNoteEditable ? (
              <>
                <TextArea
                  defaultValue={versionDetails.note?.content}
                  disabled={!isNoteEditable}
                  name="Note"
                  onChange={(event) => handleChange(event, 'note')}
                  placeholder="Write your note"
                />
                <UploadFilesBlock
                  fileType="note"
                  files={noteFiles}
                  onDrop={handleNoteFilesDrop}
                  onRemove={(fileType: TFileType, id: string) => handleDeleteFile(fileType, id)}
                />
                <div className="button-container">
                  <div className="button-container__right-buttons">
                    <Button disabled={isVersionDetailsLoading || note === ''} onClick={handleSubmitNote} size="small">
                      Save Note
                    </Button>
                    <Button ghost onClick={() => handleResetNote()} size="small">
                      Cancel
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="note-container">
                {noteFiles.map(({ download_url, id, mime_type, name }) => (
                  <MediaCard
                    downloadUrl={download_url}
                    fileName={name}
                    isLoading={isNoteFilesDetailsLoading}
                    key={id}
                    mimeType={mime_type}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="section-title">
          Comments
          {versionCommentsList && versionCommentsList.total > 0 ? <Badge value={versionCommentsList.total} /> : null}
        </div>
        <div className="section-content__comments">
          <TextInput
            className="comment-text-input"
            onChange={(event) => handleChange(event, 'comment')}
            placeholder="Write your comment"
            value={comment}
          />
          <UploadFilesBlock
            fileType="comment"
            files={commentFiles}
            onDrop={handleCommentFilesDrop}
            onRemove={(fileType: TFileType, id: string) => handleDeleteFile(fileType, id)}
          />
          <div className="button-container">
            <div className="button-container__right-buttons">
              <Button disabled={isVersionDetailsLoading || comment === ''} onClick={handleSubmitComment} size="small">
                Send Comment
              </Button>
              <Button ghost onClick={handleResetComment} size="small">
                Cancel
              </Button>
            </div>
          </div>
          {versionCommentsList?.data?.map(({ comment_id, content, created_at, created_by, file_ids }) => {
            return (
              <div className="comment" key={comment_id}>
                <UserCardWithCall createdAt={created_at} userId={created_by} />
                <div className="comment__content">{content}</div>
                <MediaCardWithCall className="with-margin" mediaIds={file_ids} />
                {currentUserProfile?.user_id === created_by ? (
                  <span className="comment__delete" onClick={handleDeleteComment(comment_id)}>
                    Delete
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
      <ModalDialog
        isOpen={isConformationModalOpen}
        mainText="Are you sure you want to delete this comment ? "
        onClose={() => handleDialogModalState(false)}
        onConfirm={handleDeleteCommentConfirm}
      />
    </div>
  ) : (
    <div>Version not found</div>
  )
}

export default PanelContent
